import { useState, type ReactNode } from 'react'
import { SlidersHorizontal, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

// The one filter-row shell for the app — a "full" filter (Transactions: date range, account,
// category, type, attributed-to) and a "date-only" filter (an account's transaction list) are
// different in field count, but should read as the same control: stacked labels, fields that
// go full-width and wrap on mobile, collapsed behind a toggle below the sm breakpoint so a
// five-field bar doesn't push the actual content off screen on a phone.
//
// `variant="card"` (default) draws its own panel — use it as a standalone bar above a list.
// `variant="inline"` drops the panel chrome for embedding inside an existing card header.
export function FilterBar({
  children,
  hasActiveFilters = false,
  variant = 'card',
  className,
}: {
  children: ReactNode
  hasActiveFilters?: boolean
  variant?: 'card' | 'inline'
  className?: string
}) {
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <div
      className={cn(
        variant === 'card' &&
          'mb-5 rounded-xl border border-[var(--fl-border)] bg-[var(--fl-surface)] p-4 shadow-sm',
        className
      )}
    >
      <button
        type="button"
        onClick={() => setMobileOpen((open) => !open)}
        aria-expanded={mobileOpen}
        className="flex w-full items-center justify-between text-sm font-medium text-[var(--fl-text-1)] sm:hidden"
      >
        <span className="flex items-center gap-2">
          <SlidersHorizontal size={14} />
          Filters
          {hasActiveFilters && (
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--fl-heading)]" aria-hidden="true" />
          )}
        </span>
        <ChevronDown size={16} className={cn('transition-transform', mobileOpen && 'rotate-180')} />
      </button>

      {/* Layout of the fields themselves is left to the caller (e.g. two stacked rows for a
          date-group + lookup-fields split) — this just owns the card and the mobile collapse. */}
      <div className={cn(mobileOpen ? 'mt-3 block' : 'hidden', 'sm:mt-0 sm:block')}>{children}</div>
    </div>
  )
}

// A single flex-wrap row of FilterFields — most filter bars are just one of these; a "full"
// bar (Transactions) stacks two, one for date controls and one for lookup selects.
export function FilterRow({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex flex-wrap items-end gap-3', className)}>{children}</div>
}

// One field: a stacked label over its control, full-width on mobile so it doesn't get
// squeezed, auto-width from sm up so several sit in a row.
export function FilterField({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: ReactNode
}) {
  return (
    <div className={cn('w-full min-w-0 sm:w-auto', className)}>
      <label className="mb-1 block text-xs font-medium text-[var(--fl-text-2)]">{label}</label>
      {children}
    </div>
  )
}

export function FilterClearButton({
  onClick,
  label = 'Clear filters',
}: {
  onClick: () => void
  label?: string
}) {
  return (
    <Button
      type="button"
      variant="outline"
      onClick={onClick}
      className="h-auto rounded-lg border-[var(--fl-border)] bg-transparent px-3 py-2.5 text-sm text-[var(--fl-text-2)] hover:bg-transparent hover:text-[var(--fl-error)]"
    >
      {label}
    </Button>
  )
}
